import type { PostHog } from "posthog-js";

type IdentifyProperties = Record<string, unknown>;

// Module-level guard state. PostHog identity is a page-global singleton, so
// the guard must be global too: a per-component ref would not dedupe across
// the many components/observers that mount the org hooks simultaneously.
// Logout always performs a full page navigation (see Sidebar handleLogout and
// the _app.tsx error boundary), which resets this module state along with the
// rest of the JS heap, so a re-login is identified again as expected.
let lastIdentifiedId: string | null = null;
let lastPropertiesJson: string | null = null;

/**
 * Identify the current user with PostHog exactly once per distinct ID.
 *
 * - First call for a given ID fires `posthog.identify`.
 * - Subsequent calls with the same ID but changed properties (e.g. the active
 *   org after an org switch) update person properties via
 *   `setPersonProperties` without re-sending an `$identify` event.
 * - Calls with the same ID and unchanged properties are no-ops, so callers in
 *   react-query `onSuccess` callbacks (which fire once per mounted observer)
 *   can call this freely.
 */
export function identifyUser(
  posthog: PostHog | null | undefined,
  distinctId: string | null | undefined,
  properties: IdentifyProperties,
): void {
  if (!posthog || !distinctId) {
    return;
  }

  const propertiesJson = JSON.stringify(properties);

  if (distinctId !== lastIdentifiedId) {
    posthog.identify(distinctId, properties);
    lastIdentifiedId = distinctId;
    lastPropertiesJson = propertiesJson;
    return;
  }

  if (propertiesJson !== lastPropertiesJson) {
    posthog.setPersonProperties(properties);
    lastPropertiesJson = propertiesJson;
  }
}

/** Test-only helper to clear the module-level guard between test cases. */
export function resetIdentifyGuardForTests(): void {
  lastIdentifiedId = null;
  lastPropertiesJson = null;
}
